import { NavigateFunction } from 'react-router-dom';
import { Dispatch, SetStateAction } from 'react';
import { axiosInstance } from 'src/utils/axiosInstance.utils';
import {
  apiUrls,
  dataStorageKeywords,
  RESEND_EXPIRATION_TIME,
  routeURL,
} from 'src/utils/properties.utils';
import { setEncryptedLocalStorageData } from 'src/utils/dataStorageManagement.utils';
import { handleEmailError } from 'src/utils/errorHandler';

interface VerifyEmailParams {
  token: string | null;
  setMessage: (message: string) => void;
  setSeverity: (severity: 'success' | 'error') => void;
  setIsButtonDisabled: (disabled: boolean) => void;
  setRemainingTime: Dispatch<SetStateAction<number>>;
  navigate: NavigateFunction;
  setExpired: (expired: boolean) => void;
}

export const verifyEmail = async ({
  token,
  setMessage,
  setSeverity,
  setIsButtonDisabled,
  setRemainingTime,
  navigate,
  setExpired,
}: VerifyEmailParams) => {
  if (token) {
    try {
      const response = await axiosInstance.get(apiUrls.VERIFY_EMAIL, {
        params: { token },
      });
      setMessage(response.data.message);
      setSeverity('success');
      setIsButtonDisabled(true);
      setRemainingTime(0);
      localStorage.removeItem(dataStorageKeywords.TIMER_END_TIME);

      setTimeout(() => {
        navigate(routeURL.LOGIN);
      }, 2000);
      return;
    } catch (error: any) {
      handleEmailError({
        error,
        setMessage,
        setSeverity,
        setIsButtonDisabled,
        setRemainingTime,
        navigate,
        setExpired,
      });
    }
  }

  const endTimeKey = dataStorageKeywords.TIMER_END_TIME;
  const savedEndTime = localStorage.getItem(endTimeKey);
  const savedRemainingTime = localStorage.getItem(
    dataStorageKeywords.TIMER_REMAINING_TIME
  );

  if (savedEndTime && savedRemainingTime) {
    const endTime = parseInt(savedEndTime, 10);
    const currentTime = Math.floor(Date.now() / 1000);
    const timeDiff = endTime - currentTime;

    if (timeDiff > 0) {
      setRemainingTime(timeDiff);
      setIsButtonDisabled(true);
    } else {
      setRemainingTime(0);
      setIsButtonDisabled(false);
    }
  } else {
    const initialTime = savedRemainingTime
      ? parseInt(savedRemainingTime, 10)
      : RESEND_EXPIRATION_TIME;
    setRemainingTime(initialTime);
    setIsButtonDisabled(true);
  }

  const interval = setInterval(() => {
    setRemainingTime((prevTime: number) => {
      if (prevTime <= 1) {
        clearInterval(interval);
        setIsButtonDisabled(false);
        localStorage.removeItem(endTimeKey);
        localStorage.removeItem(dataStorageKeywords.TIMER_REMAINING_TIME);
        return 0;
      }
      const newTime = prevTime - 1;
      setEncryptedLocalStorageData(
        dataStorageKeywords.TIMER_REMAINING_TIME,
        newTime.toString()
      );
      return newTime;
    });
  }, 1000);

  return () => clearInterval(interval);
};

export const formatTime = (time: number): string => {
  const minutes = Math.floor(time / 60);
  const seconds = time % 60;
  return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
};
